import React, { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

interface MarkdownRendererProps {
    content: string;
    className?: string;
}

/**
 * Renders markdown content into sanitized HTML.
 * Uses `marked` for parsing and `DOMPurify` for XSS protection.
 */
export const MarkdownRenderer = ({ content, className }: MarkdownRendererProps) => {
    const html = useMemo(() => {
        if (!content) {return '';}
        
        // Configure marked for better line-break handling (essential for the lesson body style)
        marked.setOptions({
            breaks: true,
            gfm: true,
        });

        const rawHtml = marked.parse(content) as string;
        return DOMPurify.sanitize(rawHtml, { 
            ADD_ATTR: ['target'] // Allow target="_blank" for external links
        });
    }, [content]);

    if (!html) {
        return <div className={`text-slate-400 text-sm italic ${className || ''}`}>No content available.</div>;
    }

    return (
        <div 
            className={`prose prose-sm max-w-none prose-slate 
                prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight
                prose-p:leading-relaxed prose-p:text-slate-700
                prose-strong:text-slate-900 prose-strong:font-black
                prose-li:text-slate-700
                ${className || ''}`} 
            dangerouslySetInnerHTML={{ __html: html }} 
        />
    );
};
